/**
 * DEV-2521: property-zone timestamp construction and time-option intersection for
 * maintenance blocks.
 *
 * All conversions use the property's IANA zone through moment-timezone. Browser local time
 * is never consulted, nothing is rounded, and unsupported values are rejected.
 */
import moment from 'moment-timezone';

export type LocalDate = string; // YYYY-MM-DD in the property zone
export type LocalTime = string; // HH:mm (24h)

export interface AvailabilityWindow {
  /** UTC ISO instant, inclusive. */
  start: string;
  /** UTC ISO instant, exclusive. */
  end: string;
}

export interface ReservationAvailability {
  listing_id: string;
  timezone: string;
  check_in_time: LocalTime;
  check_out_time: LocalTime;
  allowed_start_times: LocalTime[];
  allowed_end_times: LocalTime[];
  available_windows: AvailabilityWindow[];
}

const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const LOCAL_FORMAT = 'YYYY-MM-DD HH:mm';
/** Explicit-offset ISO 8601 with seconds, e.g. `2027-01-12T11:00:00-10:00`. */
const OFFSET_ISO_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';

export function isLocalDate(value: unknown): value is LocalDate {
  return typeof value === 'string' && LOCAL_DATE_PATTERN.test(value) && moment(value, 'YYYY-MM-DD', true).isValid();
}

export function isLocalTime(value: unknown): value is LocalTime {
  return typeof value === 'string' && LOCAL_TIME_PATTERN.test(value);
}

/**
 * Resolve a local date + time in the property zone to an instant.
 *
 * Returns `null` for malformed input, an unknown zone, a nonexistent local time (spring
 * forward gap) or an ambiguous local time (fall back overlap). v1 has no offset selector, so
 * both DST edge cases are simply unselectable.
 */
export function resolvePropertyZoneInstant(
  date: LocalDate,
  time: LocalTime,
  timezone: string
): moment.Moment | null {
  const ms = resolveLocalMs(date, time, timezone);
  return ms === null ? null : moment.tz(ms, timezone);
}

const MAX_CACHE_ENTRIES = 4000;
const localMsCache = new Map<string, number | null>();

/**
 * Cached epoch-millisecond resolution of a property-local date/time. The picker evaluates
 * many dates against many picklist times, so this keeps disabled-date checks cheap.
 */
export function resolveLocalMs(date: LocalDate, time: LocalTime, timezone: string): number | null {
  const key = `${timezone}|${date}|${time}`;
  const cached = localMsCache.get(key);
  if (cached !== undefined) {
    return cached;
  }
  const value = computeLocalMs(date, time, timezone);
  if (localMsCache.size >= MAX_CACHE_ENTRIES) {
    localMsCache.clear();
  }
  localMsCache.set(key, value);
  return value;
}

function computeLocalMs(date: LocalDate, time: LocalTime, timezone: string): number | null {
  if (!isLocalDate(date) || !isLocalTime(time) || !timezone || !moment.tz.zone(timezone)) {
    return null;
  }
  const local = `${date} ${time}`;
  const resolved = moment.tz(local, LOCAL_FORMAT, true, timezone);
  if (!resolved.isValid()) {
    return null;
  }
  // Nonexistent local time: moment shifts forward, so the wall clock no longer matches.
  if (resolved.format(LOCAL_FORMAT) !== local) {
    return null;
  }
  // Ambiguous local time: the same wall clock also exists one hour earlier or later.
  const oneHourLater = resolved.clone().add(1, 'hour');
  const oneHourEarlier = resolved.clone().subtract(1, 'hour');
  if (
    oneHourLater.format(LOCAL_FORMAT) === local ||
    oneHourEarlier.format(LOCAL_FORMAT) === local
  ) {
    return null;
  }
  return resolved.valueOf();
}

/** Explicit-offset ISO string in the property zone, or `null` when unresolvable. */
export function buildPropertyZoneTimestamp(
  date: LocalDate,
  time: LocalTime,
  timezone: string
): string | null {
  const instant = resolvePropertyZoneInstant(date, time, timezone);
  return instant ? instant.format(OFFSET_ISO_FORMAT) : null;
}

function parseWindow(window: AvailabilityWindow): { start: number; end: number } | null {
  const start = moment.utc(window.start, moment.ISO_8601, true);
  const end = moment.utc(window.end, moment.ISO_8601, true);
  if (!start.isValid() || !end.isValid() || !start.isBefore(end)) {
    return null;
  }
  return { start: start.valueOf(), end: end.valueOf() };
}

/** Parse, drop malformed windows, and sort by start. Windows arrive merged from the server. */
export function normalizeWindows(windows: AvailabilityWindow[] | undefined | null) {
  return (windows ?? [])
    .map(parseWindow)
    .filter((window): window is { start: number; end: number } => window !== null)
    .sort((a, b) => a.start - b.start);
}

type ParsedWindow = ReturnType<typeof normalizeWindows>[number];

/** Window that contains a start instant: `start <= s < end`. */
export function findWindowContainingStart(windows: ParsedWindow[], startMs: number) {
  return windows.find((window) => window.start <= startMs && startMs < window.end) ?? null;
}

/** Window that can end an interval at `e`: `start < e <= end`. */
export function findWindowContainingEnd(windows: ParsedWindow[], endMs: number) {
  return windows.find((window) => window.start < endMs && endMs <= window.end) ?? null;
}

/** A block must be non-empty and fit inside exactly one free interval. */
export function intervalFitsOneWindow(
  windows: ParsedWindow[],
  startMs: number,
  endMs: number
): boolean {
  if (!(startMs < endMs)) {
    return false;
  }
  const window = findWindowContainingStart(windows, startMs);
  return !!window && endMs <= window.end;
}

/**
 * Start choices for a date: supported picklist values whose instant lies inside a window and
 * from which at least one supported end (on the same or a later day inside that window) is
 * reachable.
 */
export function getStartTimeOptions(
  availability: ReservationAvailability,
  startDate: LocalDate,
  nowMs: number = Date.now()
): LocalTime[] {
  const windows = normalizeWindows(availability.available_windows);
  return availability.allowed_start_times.filter((time) => {
    const startMs = resolveLocalMs(startDate, time, availability.timezone);
    if (startMs === null || startMs <= nowMs) return false;
    const window = findWindowContainingStart(windows, startMs);
    if (!window) return false;
    // Reachable end: any supported end time on any day between start and window end.
    return hasReachableEnd(availability, startDate, startMs, window);
  });
}

function dayHasEndAfter(
  availability: ReservationAvailability,
  date: LocalDate,
  startMs: number,
  windowEndMs: number
): boolean {
  return availability.allowed_end_times.some((time) => {
    const endMs = resolveLocalMs(date, time, availability.timezone);
    return endMs !== null && startMs < endMs && endMs <= windowEndMs;
  });
}

function hasReachableEnd(
  availability: ReservationAvailability,
  startDate: LocalDate,
  startMs: number,
  window: ParsedWindow
): boolean {
  if (availability.allowed_end_times.length === 0) return false;
  // The start day itself.
  if (dayHasEndAfter(availability, startDate, startMs, window.end)) return true;
  // The last local day touched by the window.
  const windowEndDay = moment.tz(window.end, availability.timezone);
  const lastDate = windowEndDay.format('YYYY-MM-DD');
  if (lastDate !== startDate && dayHasEndAfter(availability, lastDate, startMs, window.end)) {
    return true;
  }
  // Any full day strictly between the start day and the window's last day lies entirely
  // inside the window, so every supported end time on it is reachable.
  const nextDate = addLocalDays(startDate, 1);
  return compareLocalDates(nextDate, lastDate) < 0;
}

/**
 * End choices for a date. When the start is chosen, ends must follow it and fit the same
 * window. Without a start, any supported end that closes some window is offered so the owner
 * can pick in either order.
 */
export function getEndTimeOptions(
  availability: ReservationAvailability,
  endDate: LocalDate,
  start: { date: LocalDate; time: LocalTime } | null,
  nowMs: number = Date.now()
): LocalTime[] {
  const windows = normalizeWindows(availability.available_windows);
  let startMs: number | null = null;
  let startWindow: ParsedWindow | null = null;
  if (start && start.date && start.time) {
    startMs = resolveLocalMs(start.date, start.time, availability.timezone);
    if (startMs === null) return [];
    startWindow = findWindowContainingStart(windows, startMs);
    if (!startWindow) return [];
  }
  return availability.allowed_end_times.filter((time) => {
    const endMs = resolveLocalMs(endDate, time, availability.timezone);
    if (endMs === null || endMs <= nowMs) return false;
    if (startMs !== null && startWindow) {
      return startMs < endMs && endMs <= startWindow.end;
    }
    return findWindowContainingEnd(windows, endMs) !== null;
  });
}

/** Local dates (property zone) that have at least one selectable start time. */
export function dateHasStartOptions(
  availability: ReservationAvailability,
  date: LocalDate,
  nowMs: number = Date.now()
): boolean {
  return getStartTimeOptions(availability, date, nowMs).length > 0;
}

/** Local dates that offer at least one end time for the given (optional) start. */
export function dateHasEndOptions(
  availability: ReservationAvailability,
  date: LocalDate,
  start: { date: LocalDate; time: LocalTime } | null,
  nowMs: number = Date.now()
): boolean {
  return getEndTimeOptions(availability, date, start, nowMs).length > 0;
}

export interface MaintenanceInterval {
  startDate: LocalDate | null;
  startTime: LocalTime | null;
  endDate: LocalDate | null;
  endTime: LocalTime | null;
}

export type MaintenanceIntervalValidation =
  | { ok: true; check_in: string; check_out: string }
  | { ok: false; field: keyof MaintenanceInterval; message: string };

/**
 * Validate a complete interval against the supported choices and returned windows, and build
 * the explicit-offset request timestamps.
 */
export function validateMaintenanceInterval(
  availability: ReservationAvailability,
  interval: MaintenanceInterval,
  nowMs: number = Date.now()
): MaintenanceIntervalValidation {
  const { startDate, startTime, endDate, endTime } = interval;
  if (!startDate) return { ok: false, field: 'startDate', message: 'Please choose a start date.' };
  if (!startTime) return { ok: false, field: 'startTime', message: 'Please choose a start time.' };
  if (!endDate) return { ok: false, field: 'endDate', message: 'Please choose an end date.' };
  if (!endTime) return { ok: false, field: 'endTime', message: 'Please choose an end time.' };

  if (!availability.allowed_start_times.includes(startTime)) {
    return { ok: false, field: 'startTime', message: 'That start time is not supported.' };
  }
  if (!availability.allowed_end_times.includes(endTime)) {
    return { ok: false, field: 'endTime', message: 'That end time is not supported.' };
  }

  const start = resolvePropertyZoneInstant(startDate, startTime, availability.timezone);
  if (!start) {
    return { ok: false, field: 'startTime', message: 'That start time does not exist on the selected date.' };
  }
  const end = resolvePropertyZoneInstant(endDate, endTime, availability.timezone);
  if (!end) {
    return { ok: false, field: 'endTime', message: 'That end time does not exist on the selected date.' };
  }
  if (start.valueOf() <= nowMs) {
    return { ok: false, field: 'startTime', message: 'The block must start in the future.' };
  }
  if (!start.isBefore(end)) {
    return { ok: false, field: 'endTime', message: 'The end must be after the start.' };
  }
  const windows = normalizeWindows(availability.available_windows);
  if (!intervalFitsOneWindow(windows, start.valueOf(), end.valueOf())) {
    return {
      ok: false,
      field: 'endTime',
      message: 'The selected times are not available. Choose a block within one open period.'
    };
  }
  return {
    ok: true,
    check_in: start.format(OFFSET_ISO_FORMAT),
    check_out: end.format(OFFSET_ISO_FORMAT)
  };
}

/** `HH:mm` → `h:mm AM/PM` for labels. Pure string formatting; no zone involved. */
export function formatLocalTimeLabel(time: LocalTime): string {
  if (!isLocalTime(time)) return time;
  const [hoursText, minutes] = time.split(':');
  const hours = Number(hoursText);
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const twelveHour = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelveHour}:${minutes} ${suffix}`;
}

/** Human-friendly `January 12, 2027` for a property-local calendar date. */
export function formatLocalDateLabel(date: LocalDate): string {
  if (!isLocalDate(date)) return date;
  return moment(date, 'YYYY-MM-DD', true).format('MMMM D, YYYY');
}

/** Zone abbreviation for a date in the property zone, e.g. `HST`, `PST`, `PDT`. */
export function zoneAbbreviation(timezone: string, date: LocalDate | null): string {
  if (!timezone || !moment.tz.zone(timezone)) return '';
  const reference = date && isLocalDate(date) ? moment.tz(date, 'YYYY-MM-DD', timezone) : moment.tz(timezone);
  return reference.format('z');
}

/** Human-friendly `January 12, 2027, 11:00 AM` for a local date/time in the property zone. */
export function formatLocalDateTimeLabel(
  date: LocalDate | null,
  time: LocalTime | null,
  timezone: string
): string {
  if (!date || !time) return '';
  const instant = resolvePropertyZoneInstant(date, time, timezone);
  if (!instant) {
    return `${date} ${formatLocalTimeLabel(time)}`;
  }
  return instant.format('MMMM D, YYYY, h:mm A');
}

/** Format an instant (ISO or Date) in a named zone, e.g. `Jan 12, 2027, 11:00 AM HST`. */
export function formatInstantInZone(
  value: string | Date | null | undefined,
  timezone: string,
  pattern: string = 'MMM D, YYYY, h:mm A z'
): string {
  if (!value) return '';
  const zone = timezone && moment.tz.zone(timezone) ? timezone : 'UTC';
  const m = moment(value).tz(zone);
  return m.isValid() ? m.format(pattern) : '';
}

/** Local `YYYY-MM-DD` for "today" in the property zone (or a fallback zone). */
export function todayInZone(timezone: string, nowMs: number = Date.now()): LocalDate {
  const zone = timezone && moment.tz.zone(timezone) ? timezone : 'UTC';
  return moment.tz(nowMs, zone).format('YYYY-MM-DD');
}

/** Add calendar days to a local date string. */
export function addLocalDays(date: LocalDate, days: number): LocalDate {
  return moment(date, 'YYYY-MM-DD', true).add(days, 'days').format('YYYY-MM-DD');
}

export function compareLocalDates(a: LocalDate, b: LocalDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}
